import * as Popover from "@radix-ui/react-popover";
import { type Job, type RedactedJob, VIDEO_MODE_LABELS } from "@studio/shared";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { isActiveJob, useLive } from "@/lib/events";
import { cn, formatWait, modelLabel } from "@/lib/format";
import { Button } from "./ui";

export function jobTitle(job: Job): string {
  if (job.type === "image") return job.request.type === "image" && job.request.count > 1 ? "Images" : "Image";
  if (job.type === "music") return "Song";
  if (job.type === "speech") return "Speech";
  if (job.type === "enhance") return "Enhance";
  return job.videoMode ? VIDEO_MODE_LABELS[job.videoMode].title : "Video";
}

/** One line for where a job stands: the phrase the status pill and cards share. */
export function jobPhase(job: Job): string {
  switch (job.status) {
    case "draft":
      return "Waiting for you to start";
    case "queued":
      return job.queuePosition && job.queuePosition > 1 ? `In line, ${job.queuePosition - 1} ahead` : "Next in line";
    case "loading_model":
      return `Loading the ${modelLabel(job.model)}`;
    case "running":
      if (job.progress?.step && job.progress.totalSteps) return `Step ${job.progress.step} of ${job.progress.totalSteps}`;
      return job.progress?.phase ? capitalize(job.progress.phase) : "Working";
    case "completed":
      return "Done";
    case "failed":
      return "Failed";
    case "cancelled":
      return "Cancelled";
  }
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Someone else's job: what and how big, never the prompt. */
function queuedTitle(job: RedactedJob): string {
  const what = job.type === "image" ? (job.count && job.count > 1 ? "Images" : "Image") : job.type === "music" ? "Song" : job.type === "speech" ? "Speech" : job.type === "enhance" ? "Enhance" : "Video";
  const size = job.type === "video" ? job.resolution : job.durationSec ? `${Math.round(job.durationSec)} s` : null;
  return size ? `${what} · ${size}` : what;
}

function queuedPhase(job: RedactedJob): string {
  if (job.status === "running" || job.status === "loading_model") return job.runnerName ? `Being made on ${job.runnerName}` : "Being made now";
  if (job.queuePosition && job.queuePosition > 1) return `In line, ${job.queuePosition - 1} ahead`;
  return "Next in line";
}

function useStatusLine(): { tone: "idle" | "work" | "off"; text: string } {
  const { gpu, jobs, queue, connected } = useLive();
  const mine = Object.values(jobs).filter(isActiveJob);
  if (!connected || !gpu) return { tone: "off", text: "Connecting…" };
  const running = mine.find((j) => j.status === "running" || j.status === "loading_model");
  const others = queue.filter((q) => !q.mine).length;
  if (running) {
    const waiting = mine.length - 1 + others;
    return {
      tone: "work",
      text: `${jobTitle(running)}: ${jobPhase(running).toLowerCase()}${waiting > 0 ? ` · ${waiting} waiting` : ""}`,
    };
  }
  const busy = queue.find((q) => q.status === "running" || q.status === "loading_model");
  if (busy && !busy.mine) {
    const waiting = queue.length - 1;
    return { tone: "work", text: `${busy.ownerName}: ${queuedTitle(busy).toLowerCase()}${waiting > 0 ? ` · ${waiting} waiting` : ""}` };
  }
  if (gpu.state === "loading") return { tone: "work", text: `Loading the ${modelLabel(gpu.activeModel)}` };
  if (gpu.busyWith === "chat") return { tone: "work", text: "Answering in chat" };
  if (queue.length) return { tone: "work", text: `${queue.length} waiting` };
  return { tone: "idle", text: "Ready" };
}

/** Always-visible answer to "why is nothing happening": what the GPU servers are doing and what waits. */
export function GpuStatus({ className }: { className?: string }) {
  const { tone, text } = useStatusLine();
  const jobs = useLive((s) => s.jobs);
  const queue = useLive((s) => s.queue);
  const runners = useLive((s) => s.runners);
  const mine = Object.values(jobs).filter(isActiveJob);
  const others = queue.filter((q) => !q.mine);
  const online = runners.filter((r) => r.online).length;
  const cancel = useMutation({
    mutationFn: (id: string) => api.cancelJob(id),
    onError: (error) => toast.error(error.message),
  });
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-9 max-w-[62vw] items-center gap-2 rounded-full border border-line bg-panel pl-3 pr-3.5 text-[13px] font-medium text-paper transition-colors hover:bg-raised md:max-w-[340px]",
            className,
          )}
        >
          <span
            className={cn(
              "size-2 shrink-0 rounded-full",
              tone === "idle" && "bg-ok",
              tone === "work" && "animate-pulse bg-film",
              tone === "off" && "bg-mute",
            )}
            aria-hidden
          />
          <span className="truncate">{text}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          collisionPadding={12}
          className="z-50 w-[min(92vw,380px)] rounded-[var(--radius-control)] border border-line bg-panel p-2 shadow-2xl shadow-black/40 outline-none"
        >
          <p className="px-2 pb-2 pt-1 text-sm text-mute">
            {online > 1
              ? `${online} GPU servers share the work; each makes one thing at a time.`
              : "One model fits the graphics cards at a time, so requests take turns. Switching models takes up to a minute."}
          </p>
          {mine.length === 0 && others.length === 0 ? (
            <p className="px-2 pb-2 text-sm">Nothing is waiting.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {mine.map((job) => (
                <li key={job.id} className="flex items-center gap-3 rounded-[10px] px-2 py-2 hover:bg-raised">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{jobTitle(job)}</p>
                    <p className="tabular truncate text-[13px] text-mute">
                      {jobPhase(job)}
                      {job.etaSec ? ` · about ${formatWait(job.etaSec)}` : ""}
                    </p>
                  </div>
                  {job.status === "queued" ? (
                    <Button size="sm" variant="quiet" onClick={() => cancel.mutate(job.id)} disabled={cancel.isPending}>
                      Cancel
                    </Button>
                  ) : null}
                </li>
              ))}
              {others.map((job) => (
                <li key={job.ref} className="flex items-center gap-3 rounded-[10px] px-2 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">
                      {job.ownerName}: <span className="text-mute">{queuedTitle(job).toLowerCase()}</span>
                    </p>
                    <p className="tabular truncate text-[13px] text-mute">
                      {queuedPhase(job)}
                      {job.etaSec ? ` · about ${formatWait(job.etaSec)}` : ""}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
